import { useCallback, useContext, useEffect } from 'react'

import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

import {
  getViewportContextFromPanel,
  getViewportSelectionSummary,
} from '@/components/chat-v2/lib/panelEntrypoints'
import {
  addReplyToChat,
  readReplyQuote,
} from '@/components/chat-v2/lib/replyQuote'
import {
  addViewportSelectionToChat,
  getViewportSelectionStage,
} from '@/components/chat-v2/lib/viewportSelection'
import {
  AVAILABLE_TOOLS,
  ChatConfigurationContext,
  ChatEnvironmentContext,
  ChatEnvironmentStoreContext,
  ChatEnvironmentStoreState,
  type CompleteImageAttachment,
  type ImageAttachment,
  type PastedTextCard,
  type PendingImageAttachment,
  type QueuedImageAttachment,
  selectPersistedPanelState,
  setPersistedPanelChromeState,
} from '@/components/chat-v2/providers/ChatEnvironmentProvider'

import { ensureDateStringIsUTC } from '@/helpers/date'
import { cleanFileName } from '@/helpers/file'
import { isMac } from '@/helpers/platform'

import { useChatIsRunning } from '@/hooks/chat/useChatIsRunning'

import { UUID } from '@/types/Id'

export const useChatEnvironmentStore = <T>(
  selector: (state: ChatEnvironmentStoreState) => T = (state) => state as T,
): T => {
  const context = useContext(ChatEnvironmentStoreContext)
  if (!context) {
    throw new Error(
      'useChatEnvironmentStore must be used within an ChatEnvironmentStoreProvider',
    )
  }
  return useStore(context, selector)
}

export const useSidebarOpen = () => {
  return useChatEnvironmentStore((state) => state.sidebarOpen)
}

export const useSetSidebarOpen = () => {
  return useChatEnvironmentStore((state) => state.setSidebarOpen)
}

export const useSelectedModel = () => {
  return useChatEnvironmentStore((state) => state.selectedModel)
}

export const useModelOptions = () => {
  return useChatEnvironmentStore((state) => state.modelOptions)
}

export const useSetSelectedModel = () => {
  return useChatEnvironmentStore((state) => state.setSelectedModel)
}

export const useSelectedTools = () => {
  return useChatEnvironmentStore(useShallow((state) => state.selectedTools))
}

export const useSetSelectedTools = () => {
  return useChatEnvironmentStore((state) => state.setSelectedTools)
}

export const useAddTool = () => {
  return useChatEnvironmentStore((state) => state.addTool)
}

export const useRemoveTool = () => {
  return useChatEnvironmentStore((state) => state.removeTool)
}

// region: MCP Integration hooks

export const useSelectedIntegrations = () => {
  return useChatEnvironmentStore(
    useShallow((state) => state.selectedIntegrations),
  )
}

export const useSetSelectedIntegrations = () => {
  return useChatEnvironmentStore((state) => state.setSelectedIntegrations)
}

export const useAddIntegration = () => {
  return useChatEnvironmentStore((state) => state.addIntegration)
}

export const useRemoveIntegration = () => {
  return useChatEnvironmentStore((state) => state.removeIntegration)
}

// endregion: MCP Integration hooks

// region: Connector source hooks

export const useSelectedConnectorSources = () => {
  return useChatEnvironmentStore(
    useShallow((state) => state.selectedConnectorSources),
  )
}

export const useAddConnectorSource = () => {
  return useChatEnvironmentStore((state) => state.addConnectorSource)
}

export const useRemoveConnectorSource = () => {
  return useChatEnvironmentStore((state) => state.removeConnectorSource)
}

// endregion: Connector source hooks

export const useMakeSendMessageEvent = () => {
  return useChatEnvironmentStore((state) => state.makeSendMessageParams)
}
export const useChatContinuationRequestMessageId = () => {
  return useChatEnvironmentStore(
    (state) => state.chatContinuationRequestMessageId,
  )
}

export const useSetChatContinuationRequestMessageId = () => {
  return useChatEnvironmentStore(
    (state) => state.setChatContinuationRequestMessageId,
  )
}

export const useToolPlaceholder = () => {
  const selectedTools = useSelectedTools()
  const isCompact = useChatIsCompact()

  if (isCompact || selectedTools.length === 0) {
    return 'Send a message...'
  }

  const tool = AVAILABLE_TOOLS.find((t) => t.id === selectedTools[0])
  return tool?.placeholder || 'Send a message...'
}

export const useChatEnvironmentStoreContext = () => {
  const context = useContext(ChatEnvironmentStoreContext)
  if (!context) {
    throw new Error(
      'useChatEnvironmentContext must be used within an ChatEnvironmentProvider',
    )
  }
  return context
}

export const useChatConfigurationContext = () => {
  const context = useContext(ChatConfigurationContext)
  if (!context) {
    throw new Error(
      'useChatEnvironmentContext must be used within an ChatEnvironmentProvider',
    )
  }
  return context
}

export const useChatSessionId = () => {
  const { sessionId } = useChatConfigurationContext()
  return sessionId
}

export const useChatOrgId = () => {
  const { orgId } = useChatConfigurationContext()
  return orgId
}

export const useChatEnvironmentContext = () => {
  const context = useContext(ChatEnvironmentContext)
  if (!context) {
    throw new Error(
      'useChatEnvironmentContext must be used within an ChatEnvironmentProvider',
    )
  }
  return context
}

export const useChatAgentName = () => {
  const { agentName } = useChatEnvironmentContext()
  return agentName
}

/**
 * Returns the agent name from the chat configuration context.
 *
 * This hook is dangerous, as the chat configuration uses the agent name
 * purely for UI configuration purposes. Generally, the agent name should be fetched
 * from the chat environment context instead using {@link useChatAgentName}.
 *
 * @returns The agent name from the chat configuration context
 */
export const useChatAgentNameForUi = () => {
  const { agentNameForUi } = useChatConfigurationContext()
  return agentNameForUi
}

export const useChatPursuitId = () => {
  const { pursuitId } = useChatConfigurationContext()
  return pursuitId
}

/**
 * Pursuit id when rendered inside a chat environment, undefined otherwise.
 * For surfaces (dialogs, tool displays) that can also mount outside chat.
 */
export const useChatPursuitIdOptional = () => {
  return useContext(ChatConfigurationContext)?.pursuitId
}

export const useChatNewChatLink = () => {
  const { newChatLink } = useChatConfigurationContext()
  return newChatLink
}

export const usePrepareNewChatNavigation = () => {
  const store = useChatEnvironmentStoreContext()
  const agentNameForUi = useChatAgentNameForUi()
  const orgId = useChatOrgId()
  const pursuitId = useChatPursuitId()

  return useCallback(() => {
    const { sourceViewerState, sidebarOpen } = store.getState()
    setPersistedPanelChromeState({
      agentNameForUi,
      orgId,
      pursuitId,
      sourceViewerState,
      sidebarOpen,
    })
  }, [agentNameForUi, orgId, pursuitId, store])
}

export const useGetPersistedPanelStateSnapshot = () => {
  const store = useChatEnvironmentStoreContext()

  return useCallback(() => selectPersistedPanelState(store.getState()), [store])
}

export const useChatRecentChatsLink = () => {
  const { recentChatsLink } = useChatConfigurationContext()
  return recentChatsLink
}

export const useChatPromptsLink = () => {
  const { promptsLink } = useChatConfigurationContext()
  return promptsLink
}

export const useChatGetSessionLink = () => {
  const { getSessionLink } = useChatConfigurationContext()
  return getSessionLink
}

export const useChatIsCompact = () => {
  const { compact } = useChatEnvironmentContext()
  return compact ?? false
}

export const useChatQueryKeys = () => {
  const { queryKeys } = useChatConfigurationContext()
  return queryKeys
}

// Session history hooks
export const useSearchQuery = () => {
  return useChatEnvironmentStore((state) => state.searchQuery)
}

export const useSetSearchQuery = () => {
  return useChatEnvironmentStore((state) => state.setSearchQuery)
}

export const useSetExpandedMarkdownTableKey = () => {
  return useChatEnvironmentStore((state) => state.setExpandedMarkdownTableKey)
}

export const useSelectedSessionIds = () => {
  return useChatEnvironmentStore(
    useShallow((state) => state.selectedSessionIds),
  )
}

export const useSetSelectedSessionIds = () => {
  return useChatEnvironmentStore((state) => state.setSelectedSessionIds)
}

export const useSetSessionSelected = (id: UUID) => {
  const setSessionSelected = useChatEnvironmentStore(
    (state) => state.setSessionSelected,
  )

  return useCallback(
    (selected: boolean) => {
      setSessionSelected(id, selected)
    },
    [id, setSessionSelected],
  )
}

export const useClearSessionSelection = () => {
  return useChatEnvironmentStore((state) => state.clearSessionSelection)
}

export const useIsSessionSelected = (sessionId: UUID) => {
  return useChatEnvironmentStore((state) =>
    state.selectedSessionIds.includes(sessionId),
  )
}

export const useSubmitPending = () => {
  return useChatEnvironmentStore((state) => state.submitPending)
}

export const useSetSubmitPending = () => {
  return useChatEnvironmentStore((state) => state.setSubmitPending)
}

const selectSubmitDisabledState = (state: ChatEnvironmentStoreState) => {
  const hasPendingAttachments = state.attachments.some(
    (attachment) => attachment.status === 'pending',
  )
  const hasPendingImages = state.imageAttachments.some(
    (attachment) => attachment.status === 'pending',
  )
  const isDisabled =
    state.submitDisabled || hasPendingAttachments || hasPendingImages

  return { isDisabled, hasPendingAttachments, hasPendingImages }
}

export const useSubmitDisabled = () => {
  return useChatEnvironmentStore(
    (state) => selectSubmitDisabledState(state).isDisabled,
  )
}

export const useSetSubmitDisabled = () => {
  return useChatEnvironmentStore((state) => state.setSubmitDisabled)
}

export const useSubmitDisabledReason = () => {
  return useChatEnvironmentStore((state) => {
    const { isDisabled, hasPendingAttachments, hasPendingImages } =
      selectSubmitDisabledState(state)

    if (!isDisabled) return null
    if (hasPendingImages) return 'Images still uploading'
    if (hasPendingAttachments) return 'Files still uploading'
    return 'Sending disabled'
  })
}

export const useActiveSourceId = () => {
  return useChatEnvironmentStore((state) => state.activeSourceId)
}

export const useActiveSourceSnapshot = () => {
  return useChatEnvironmentStore((state) => {
    if (state.activeSourceId == null || !state.activeTabId) return undefined
    const activeTab =
      state.panelTabs.find((tab) => tab.id === state.activeTabId) ?? null
    const snapshot = activeTab?.nav.activeSourceSnapshot
    if (!snapshot) return undefined
    return String(snapshot.id) === String(state.activeSourceId)
      ? snapshot
      : undefined
  })
}

export const useSetActiveSourceId = () => {
  return useChatEnvironmentStore((state) => state.setActiveSourceId)
}

export const useSessionIsLoading = () => {
  return useChatEnvironmentStore((state) => state.isLoading)
}

export const useSetSessionIsLoading = () => {
  return useChatEnvironmentStore((state) => state.setIsLoading)
}

export const useIsRecording = () => {
  return useChatEnvironmentStore((state) => state.isRecording)
}

export const useSetIsRecording = () => {
  return useChatEnvironmentStore((state) => state.setIsRecording)
}

export const useTodoProgressExpanded = () => {
  return useChatEnvironmentStore((state) => state.todoProgressExpanded)
}

export const useSetTodoProgressExpanded = () => {
  return useChatEnvironmentStore((state) => state.setTodoProgressExpanded)
}

export const useFocusFilesExpanded = () => {
  return useChatEnvironmentStore((state) => state.focusFilesExpanded)
}

export const useSetFocusFilesExpanded = () => {
  return useChatEnvironmentStore((state) => state.setFocusFilesExpanded)
}

export const useAttachments = () => {
  return useChatEnvironmentStore(useShallow((state) => state.attachments))
}

export const useAttachmentIds = () => {
  return useChatEnvironmentStore(
    useShallow((state) => state.attachments.map((a) => a.id)),
  )
}

export const useAddAttachment = () => {
  return useChatEnvironmentStore((state) => state.addAttachment)
}

export const useRemoveAttachment = () => {
  return useChatEnvironmentStore((state) => state.removeAttachment)
}

export const useClearAttachments = () => {
  return useChatEnvironmentStore((state) => state.clearAttachments)
}

export const useSetAttachments = () => {
  return useChatEnvironmentStore((state) => state.setAttachments)
}

export const useConvertPendingToComplete = () => {
  return useChatEnvironmentStore((state) => state.convertPendingToComplete)
}

// region: Attachment hooks

export const useAttachment = (id: UUID) => {
  return useChatEnvironmentStore((state) =>
    state.attachments.find((a) => a.id === id),
  )
}

export const useAttachmentName = (id: UUID) => {
  return useChatEnvironmentStore((state) => {
    const attachment = state.attachments.find((a) => a.id === id)
    if (!attachment) return null

    const status = attachment.status
    switch (status) {
      case 'pending':
        return cleanFileName(attachment.file.name)
      case 'complete':
        return cleanFileName(attachment.file.name)
      default:
        const exhaustiveCheck: never = status
        throw new Error(`Unknown attachment status: ${exhaustiveCheck}`)
    }
  })
}

export const useAttachmentDate = (id: UUID) => {
  return useChatEnvironmentStore((state) => {
    const attachment = state.attachments.find((a) => a.id === id)
    if (!attachment) return null

    const status = attachment.status
    switch (status) {
      case 'pending':
        return new Date(attachment.file.lastModified)
      case 'complete':
        return new Date(ensureDateStringIsUTC(attachment.file.last_modified))
      default:
        const exhaustiveCheck: never = status
        throw new Error(`Unknown attachment status: ${exhaustiveCheck}`)
    }
  })
}

export const useAttachmentSrc = (id: UUID) => {
  return useChatEnvironmentStore((state) => {
    const attachment = state.attachments.find((a) => a.id === id)
    if (!attachment) return null

    const status = attachment.status
    switch (status) {
      case 'pending':
        return URL.createObjectURL(attachment.file)
      case 'complete':
        return attachment.file.pdf_url ?? attachment.file.url
      default:
        const exhaustiveCheck: never = status
        throw new Error(`Unknown attachment status: ${exhaustiveCheck}`)
    }
  })
}

export const useAttachmentStatus = (id: UUID) => {
  return useChatEnvironmentStore((state) => {
    const attachment = state.attachments.find((a) => a.id === id)
    if (!attachment) return null
    return attachment.status
  })
}

export const useAttachmentFile = (id: UUID) => {
  return useChatEnvironmentStore(
    useShallow((state) => {
      const attachment = state.attachments.find((a) => a.id === id)
      if (!attachment) return null
      return attachment.file
    }),
  )
}

// endregion: Attachment hooks

// region: Image attachment hooks (sent as ImageUrl content in messages)

export const MAX_IMAGE_ATTACHMENTS = 5

// Re-export types for convenience
export type {
  ImageAttachment,
  QueuedImageAttachment,
  PendingImageAttachment,
  CompleteImageAttachment,
}

export const useImageAttachments = () => {
  return useChatEnvironmentStore(useShallow((state) => state.imageAttachments))
}

export const useImageAttachmentIds = () => {
  return useChatEnvironmentStore(
    useShallow((state) => state.imageAttachments.map((a) => a.id)),
  )
}

export const useAddImageAttachment = () => {
  return useChatEnvironmentStore((state) => state.addImageAttachment)
}

export const useUpdateImageAttachment = () => {
  return useChatEnvironmentStore((state) => state.updateImageAttachment)
}

export const useRemoveImageAttachment = () => {
  return useChatEnvironmentStore((state) => state.removeImageAttachment)
}

export const useClearImageAttachments = () => {
  return useChatEnvironmentStore((state) => state.clearImageAttachments)
}

export const useSetImageAttachments = () => {
  return useChatEnvironmentStore((state) => state.setImageAttachments)
}

export const useGetImageAttachments = () => {
  return useChatEnvironmentStore((state) => state.getImageAttachments)
}

export const useImageAttachment = (id: UUID) => {
  return useChatEnvironmentStore((state) =>
    state.imageAttachments.find((a) => a.id === id),
  )
}

export const useImageAttachmentStatus = (id: UUID) => {
  return useChatEnvironmentStore((state) => {
    const attachment = state.imageAttachments.find((a) => a.id === id)
    if (!attachment) return null
    return attachment.status
  })
}

export const useImageAttachmentPreviewUrl = (id: UUID) => {
  return useChatEnvironmentStore((state) => {
    const attachment = state.imageAttachments.find((a) => a.id === id)
    if (!attachment) return null
    return attachment.previewUrl
  })
}

// endregion: Image attachment hooks

// region: Pasted text cards

export type { PastedTextCard }

export const usePastedTextCards = () => {
  return useChatEnvironmentStore(useShallow((state) => state.pastedTextCards))
}

export const usePastedTextCardsCount = () => {
  return useChatEnvironmentStore((state) => state.pastedTextCards.length)
}

export const useAddPastedTextCard = () => {
  return useChatEnvironmentStore((state) => state.addPastedTextCard)
}

export const useAddPlaysCards = () => {
  return useChatEnvironmentStore((state) => state.addPlaysCards)
}

export const useUpdatePastedTextCard = () => {
  return useChatEnvironmentStore((state) => state.updatePastedTextCard)
}

export const useRemovePastedTextCard = () => {
  return useChatEnvironmentStore((state) => state.removePastedTextCard)
}

export const useClearPastedTextCards = () => {
  return useChatEnvironmentStore((state) => state.clearPastedTextCards)
}

export const useSetPastedTextCards = () => {
  return useChatEnvironmentStore((state) => state.setPastedTextCards)
}

export const useGetPastedTextCards = () => {
  return useChatEnvironmentStore((state) => state.getPastedTextCards)
}

// endregion: Pasted text cards

// region: Source viewer hooks

const getSourceViewerConfig = (state: ChatEnvironmentStoreState) => {
  if (!state.activeTabId) return null
  const tab = state.panelTabs.find((t) => t.id === state.activeTabId)
  if (!tab) return null
  const nav = tab.nav
  if (nav?.view !== 'detail' || !nav.fileId || !nav.fileType) return null
  return { id: nav.fileId, type: nav.fileType }
}

export const useSourceViewerState = () => {
  return useChatEnvironmentStore((state) => state.sourceViewerState)
}

export const useSetSourceViewerState = () => {
  return useChatEnvironmentStore((state) => state.setSourceViewerState)
}

/**
 * Whether this chip's capture is the one lit in the viewer. Boolean-narrow on
 * purpose: the subscriber re-renders only when its own activeness flips, and
 * only chips subscribe — never the messages around them.
 */
export const useIsActiveSelectionChip = (signature: string | null) => {
  return useChatEnvironmentStore(
    (state) =>
      signature !== null && state.activeSelectionChip?.signature === signature,
  )
}

export const useSetActiveSelectionChip = () => {
  return useChatEnvironmentStore((state) => state.setActiveSelectionChip)
}

export const useDismissActiveSelectionChip = () => {
  return useChatEnvironmentStore((state) => state.dismissActiveSelectionChip)
}

export const useSessionHistoryOpen = () => {
  return useChatEnvironmentStore((state) => state.sessionHistoryOpen)
}

export const useSetSessionHistoryOpen = () => {
  return useChatEnvironmentStore((state) => state.setSessionHistoryOpen)
}

export const useSourceViewerConfig = () => {
  return useChatEnvironmentStore(
    useShallow((state) => getSourceViewerConfig(state)),
  )
}

export const useSourceViewerFileId = () => {
  return useChatEnvironmentStore(
    (state) => getSourceViewerConfig(state)?.id ?? null,
  )
}

export const useSourceViewerType = () => {
  return useChatEnvironmentStore(
    (state) => getSourceViewerConfig(state)?.type ?? null,
  )
}

// endregion: Source viewer hooks

// region: Panel tab hooks

export const usePanelTabs = () => {
  return useChatEnvironmentStore(useShallow((state) => state.panelTabs))
}

export const useActiveTabId = () => {
  return useChatEnvironmentStore((state) => state.activeTabId)
}

export const useSetActiveTabId = () => {
  return useChatEnvironmentStore((state) => state.setActiveTabId)
}

export const useAddPanelTab = () => {
  return useChatEnvironmentStore((state) => state.addPanelTab)
}

export const useRemovePanelTab = () => {
  return useChatEnvironmentStore((state) => state.removePanelTab)
}

export const useReorderPanelTabs = () => {
  return useChatEnvironmentStore((state) => state.reorderPanelTabs)
}

export const useReplacePanelTabEntrypoint = () => {
  return useChatEnvironmentStore((state) => state.replacePanelTabEntrypoint)
}

export const useShowNewTabMenu = () => {
  const activeTabId = useActiveTabId()
  const addPanelTab = useAddPanelTab()
  const replacePanelTabEntrypoint = useReplacePanelTabEntrypoint()
  return useCallback(() => {
    if (activeTabId) {
      replacePanelTabEntrypoint(activeTabId, 'home')
      return
    }
    addPanelTab('home', { reuseExisting: false })
  }, [activeTabId, addPanelTab, replacePanelTabEntrypoint])
}

export const useUpdateTabNavById = () => {
  return useChatEnvironmentStore((state) => state.updateTabNavById)
}

export const useResetPanelTabs = () => {
  return useChatEnvironmentStore((state) => state.resetPanelTabs)
}

export const useActiveTab = () => {
  return useChatEnvironmentStore(
    useShallow((state) => {
      if (!state.activeTabId) return null
      return state.panelTabs.find((t) => t.id === state.activeTabId) ?? null
    }),
  )
}

/**
 * Summary of the selection inside the active viewport tab (e.g. highlighted
 * PDF text, a spreadsheet range), or null when nothing is selected. Reads the
 * store's viewer-state copy, which trails the viewer bridge by at most a
 * throttle tick — fine for an indicator; the send path reads the synchronous
 * bridge.
 *
 * `useShallow` keeps the returned `{ fileName, label }` referentially inert:
 * unrelated store updates (streaming, resize scale commits) shallow-compare
 * equal and never re-render subscribers.
 */
export const useViewportSelectionSummary = () => {
  return useChatEnvironmentStore(
    useShallow((state) => {
      const tab = state.activeTabId
        ? state.panelTabs.find((t) => t.id === state.activeTabId)
        : null
      if (!tab) return null
      return getViewportSelectionSummary(
        getViewportContextFromPanel({
          tab,
          tabs: state.panelTabs,
          sourceViewerOpen: state.sourceViewerState === 'open',
          viewerState: state.viewerStateByTabId[tab.id],
        }),
      )
    }),
  )
}

/**
 * Lifecycle stage of the active viewport selection (`none` / `selected` /
 * `staged`). The pill renders only in `selected`; once the selection is
 * staged as a composer card it disappears until the selection changes or
 * the chip is removed.
 */
export const useViewportSelectionStage = () => {
  return useChatEnvironmentStore((state) => getViewportSelectionStage(state))
}

/**
 * Global add-to-chat shortcut: ⌘L on Mac, Ctrl+Shift+L elsewhere.
 * Snapshots words picked in one of chat's finished answers into a reply chip,
 * else the active viewport selection into a selection card, and hands the
 * keyboard to the composer. Mounted once per chat surface (the
 * composer) — a per-viewer mount would add one card per mounted viewer copy.
 *
 * The shortcut always claims the key inside the chat surface: a press with
 * no live selection (or an already-staged one) still focuses the composer
 * instead of falling through to the browser's address bar, so ⌘L feels like
 * one consistent "talk about this" gesture. On Windows/Linux, plain Ctrl+L
 * stays with the address bar; the shortcut carries Shift.
 */
export const useAddViewportSelectionShortcut = () => {
  const store = useChatEnvironmentStoreContext()
  const running = useChatIsRunning()
  useEffect(() => {
    const handleKeyDown = (event: Event) => {
      // autofill dispatches a plain `Event` named "keydown", with no `key`
      if (!(event instanceof KeyboardEvent)) return
      if (event.key.toLowerCase() !== 'l' || event.altKey) return
      const matches = isMac()
        ? event.metaKey && !event.ctrlKey && !event.shiftKey
        : event.ctrlKey && !event.metaKey && event.shiftKey
      if (!matches) return
      event.preventDefault()
      const quote = readReplyQuote(document.body, running)
      if (quote) addReplyToChat(store, quote)
      else addViewportSelectionToChat(store)
      store.getState().requestComposerFocus()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [store, running])
}

/** Nonce the composer textarea watches to grab focus after add-to-chat. */
export const useComposerFocusNonce = () => {
  return useChatEnvironmentStore((state) => state.composerFocusNonce)
}

// endregion: Panel tab hooks

// region: Full screen viewer hooks

export const useFullScreenViewerOpen = () => {
  return useChatEnvironmentStore((state) => state.fullScreenViewerOpen)
}

export const useSetFullScreenViewerOpen = () => {
  return useChatEnvironmentStore((state) => state.setFullScreenViewerOpen)
}

export const useFullScreenViewerFileId = () => {
  return useChatEnvironmentStore((state) => state.fullScreenViewerFileId)
}

export const useSetFullScreenViewerFileId = () => {
  return useChatEnvironmentStore((state) => state.setFullScreenViewerFileId)
}

export const useOpenFullScreenViewer = () => {
  return useChatEnvironmentStore((state) => state.openFullScreenViewer)
}

export const useCloseFullScreenViewer = () => {
  return useChatEnvironmentStore((state) => state.closeFullScreenViewer)
}

export const usePanelExpanded = () => {
  return useChatEnvironmentStore((state) => state.panelExpanded)
}

export const useSetPanelExpanded = () => {
  return useChatEnvironmentStore((state) => state.setPanelExpanded)
}

// endregion: Full screen viewer hooks

// region: Panel animation hooks (for virtualization optimization)

export const useSetIsPanelAnimating = () => {
  return useChatEnvironmentStore((state) => state.setIsPanelAnimating)
}

export const useSetIsSidebarAnimating = () => {
  return useChatEnvironmentStore((state) => state.setIsSidebarAnimating)
}

// endregion: Panel animation hooks
